/*
 * PlantUpdatesPage — Update-Tab in der Plant-Detailseite.
 *
 * Master-Detail-Layout: links eine Session-Liste (alle Rollouts, die diese
 * Anlage je gesehen hat, per (rolloutId, clientId) aggregiert), rechts eine
 * Timeline aller Reports der aktuell selektierten Session.
 *
 * Datenquellen:
 *   - GET /api/plants/:id/rollouts        -> Session-Liste (limit 100)
 *   - GET /api/rollouts/:rolloutId/reports -> Report-Timeline pro Session
 *
 * Auto-Refresh:
 *   - Session-Liste: alle 15 s (billig, kein Log-Stream).
 *   - Report-Timeline: alle 3 s SOLANGE der neueste Report nicht terminal
 *     (Applied/Failed/RejectedSignatureInvalid). Sobald terminal, Polling
 *     stoppt automatisch bis der User eine andere Session waehlt.
 *
 * Empty-States:
 *   - Anlage hat noch nie einen Rollout gesehen: Hinweis auf Artefakte & OTA.
 *   - Keine Session ausgewaehlt: Prompt "Session links auswaehlen".
 *
 * Styling: rein ueber semantische Design-Tokens (styles/tokens.css).
 */

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { PlantSubNav } from '../components/PlantSubNav';
import { useAsync } from '../hooks/useAsync';
import { api } from '../api/client';
import { showToast } from '../components/Toaster';
import type {
  ArtifactKind,
  ListPlantRolloutsResponse,
  ListRolloutReportsResponse,
  PlantRolloutSummaryDto,
  RolloutReportDto,
  RolloutTargetStatus,
} from '../api/types';
import './PlantUpdatesPage.css';

interface Props { id?: string }

export function PlantUpdatesPage({ id }: Props) {
  if (!id) {
    return (
      <AppShell title="Update">
        <p class="hv-updates__empty">Keine Anlagen-ID in der URL.</p>
      </AppShell>
    );
  }

  // Session-Liste: alle 15 s ein Reload, damit ein frisch getriggerter Rollout
  // aus einem anderen Tab (z. B. Artefakte-Seite) hier auftaucht ohne dass der
  // Techniker die Seite neu laden muss.
  const [listTick, setListTick] = useState(0);
  useEffect(() => {
    const handle = window.setInterval(() => setListTick((t) => t + 1), 15000);
    return () => window.clearInterval(handle);
  }, []);

  const listState = useAsync(
    (signal) => api.get<ListPlantRolloutsResponse>(
      `/api/plants/${id}/rollouts?limit=100`, signal,
    ),
    [id, listTick],
  );

  const sessions: PlantRolloutSummaryDto[] = useMemo(() => {
    if (listState.status !== 'ok') return [];
    return listState.data.rollouts;
  }, [listState]);

  // Session-Auswahl. Key = rolloutId + clientId (theoretisch koennte derselbe
  // Rollout mehrere Clients treffen; unser Backend liefert eine Zeile pro
  // Kombination). Auto-Select des Top-Eintrags sobald die Liste steht und
  // noch nichts ausgewaehlt ist.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  useEffect(() => {
    if (selectedKey !== null) return;
    if (sessions.length === 0) return;
    const first = sessions[0];
    setSelectedKey(sessionKey(first));
  }, [sessions, selectedKey]);

  const selected = useMemo(
    () => sessions.find((s) => sessionKey(s) === selectedKey) ?? null,
    [sessions, selectedKey],
  );

  return (
    <AppShell title="Update">
      <PlantSubNav plantId={id} />
      <div class="hv-updates">
        <div class="hv-updates__master">
          <div class="hv-updates__master-head">
            <span class="hv-updates__master-title">Rollout-Sessions</span>
            {sessions.length > 0 && (
              <span class="hv-updates__master-count">{sessions.length}</span>
            )}
          </div>
          <div class="hv-updates__master-list">
            {listState.status === 'loading' && (
              <p class="hv-updates__hint">Lade Sessions...</p>
            )}
            {listState.status === 'error' && (
              <p class="hv-updates__hint hv-updates__hint--error">
                Sessions konnten nicht geladen werden.
              </p>
            )}
            {listState.status === 'ok' && sessions.length === 0 && (
              <div class="hv-updates__empty-block">
                <p>Fuer diese Anlage gibt es noch keine Update-Sessions.</p>
                <p class="hv-updates__hint">
                  Neue Rollouts lassen sich unter{' '}
                  <a href="/settings/artifacts">Artefakte &amp; OTA</a> ausloesen.
                </p>
              </div>
            )}
            {sessions.map((s) => (
              <SessionCard
                key={sessionKey(s)}
                session={s}
                selected={sessionKey(s) === selectedKey}
                onSelect={() => setSelectedKey(sessionKey(s))}
              />
            ))}
          </div>
        </div>

        <div class="hv-updates__detail">
          {selected ? (
            <SessionDetail key={sessionKey(selected)} session={selected} />
          ) : (
            <div class="hv-updates__detail-placeholder">
              <p>Session links auswaehlen um Details zu sehen.</p>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function sessionKey(s: PlantRolloutSummaryDto): string {
  return `${s.rolloutId}::${s.clientId}`;
}

// --- Session-Kachel (Master) ----------------------------------------------

function SessionCard({
  session,
  selected,
  onSelect,
}: {
  session: PlantRolloutSummaryDto;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      class={'hv-updates__card' + (selected ? ' hv-updates__card--selected' : '')}
      onClick={onSelect}
      title={`RolloutId ${session.rolloutId}`}
    >
      <div class="hv-updates__card-head">
        <ArtifactKindIcon kind={session.artifactKind} />
        <span class="hv-updates__card-kind">{formatKind(session.artifactKind)}</span>
        <StatusBadge status={session.latestStatus} />
      </div>
      <div class="hv-updates__card-title">
        <span class="hv-updates__card-artifact">{session.artifactDisplayName}</span>
        <span class="hv-updates__card-version">{session.artifactVersion}</span>
      </div>
      <div class="hv-updates__card-meta">
        <span class="hv-updates__card-client" title={session.clientId}>
          {session.clientDisplayName}
        </span>
        <span class="hv-updates__card-time" title={new Date(session.lastSeenAt).toLocaleString()}>
          {formatRelative(session.lastSeenAt)}
        </span>
      </div>
    </button>
  );
}

// --- Detail-Panel (Timeline) ----------------------------------------------

function SessionDetail({ session }: { session: PlantRolloutSummaryDto }) {
  const [reports, setReports] = useState<RolloutReportDto[]>([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const pollHandle = useRef<number | null>(null);

  // Reports-Polling: alle 3 s solange der letzte Report nicht terminal ist.
  // Wechselt der User die Session, wird der Effect durch [session.rolloutId]-
  // Dependency abgerissen und startet frisch.
  useEffect(() => {
    let cancelled = false;
    setReports([]);
    setInitialLoaded(false);
    setErrored(false);

    const tick = async () => {
      try {
        const res = await api.get<ListRolloutReportsResponse>(
          `/api/rollouts/${session.rolloutId}/reports`,
        );
        if (cancelled) return;
        // Server sortiert bereits absteigend (neueste zuerst).
        setReports(res.reports);
        setInitialLoaded(true);
        setErrored(false);
        const latest = res.reports[0];
        if (latest && isTerminal(latest.status) && pollHandle.current !== null) {
          window.clearInterval(pollHandle.current);
          pollHandle.current = null;
        }
      } catch {
        // Silent recovery: bei Netzwerkfehler die alten Reports sichtbar
        // lassen. Nur beim allerersten Fetch das Error-Feedback zeigen.
        if (!cancelled) setErrored((prev) => (initialLoaded ? prev : true));
      }
    };
    void tick();
    pollHandle.current = window.setInterval(tick, 3000);
    return () => {
      cancelled = true;
      if (pollHandle.current !== null) {
        window.clearInterval(pollHandle.current);
        pollHandle.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.rolloutId, session.clientId]);

  const copyRolloutId = async () => {
    try {
      await navigator.clipboard.writeText(session.rolloutId);
      showToast({ tone: 'success', title: 'RolloutId kopiert', autoHideAfterMs: 1500 });
    } catch {
      showToast({ tone: 'warn', title: 'Kopieren nicht moeglich', autoHideAfterMs: 2500 });
    }
  };

  const latest = reports[0];
  const isPolling = latest ? !isTerminal(latest.status) : true;

  return (
    <div class="hv-updates__detail-panel">
      <div class="hv-updates__detail-head">
        <div class="hv-updates__detail-head-main">
          <div class="hv-updates__detail-kind">
            <ArtifactKindIcon kind={session.artifactKind} />
            <span class="hv-updates__detail-kind-label">
              {formatKind(session.artifactKind)}
            </span>
          </div>
          <h2 class="hv-updates__detail-title">
            {session.artifactDisplayName}{' '}
            <span class="hv-updates__detail-version">{session.artifactVersion}</span>
          </h2>
          <div class="hv-updates__detail-sub">
            <span>Client: <strong>{session.clientDisplayName}</strong></span>
            <span class="hv-updates__detail-sep">/</span>
            <span title={new Date(session.firstSeenAt).toLocaleString()}>
              gestartet {formatRelative(session.firstSeenAt)}
            </span>
          </div>
        </div>
        <div class="hv-updates__detail-head-side">
          <StatusBadge status={latest?.status ?? session.latestStatus} large />
          <div class="hv-updates__rollout-id">
            <span class="hv-updates__rollout-id-label">RolloutId</span>
            <code class="hv-updates__rollout-id-value" title={session.rolloutId}>
              {session.rolloutId.slice(0, 8)}...
            </code>
            <button type="button" class="hv-updates__copy" onClick={copyRolloutId}>
              Kopieren
            </button>
          </div>
        </div>
      </div>

      <div class="hv-updates__timeline-head">
        <span>Logs</span>
        <span class="hv-updates__timeline-sub">
          {isPolling ? 'Live-Refresh alle 3 s' : 'Session abgeschlossen'}
        </span>
      </div>

      {!initialLoaded && !errored && (
        <p class="hv-updates__hint">Lade Reports...</p>
      )}
      {errored && (
        <p class="hv-updates__hint hv-updates__hint--error">
          Reports konnten nicht geladen werden.
        </p>
      )}
      {initialLoaded && reports.length === 0 && (
        <p class="hv-updates__hint">Noch keine Reports fuer diese Session.</p>
      )}

      {reports.length > 0 && (
        <ol class="hv-updates__timeline">
          {reports.map((r) => (
            <TimelineEntry key={r.id} report={r} />
          ))}
        </ol>
      )}
    </div>
  );
}

function TimelineEntry({ report }: { report: RolloutReportDto }) {
  const tone = statusTone(report.status);
  return (
    <li class={`hv-updates__timeline-item hv-updates__timeline-item--${tone}`}>
      <span class="hv-updates__timeline-dot" aria-hidden="true" />
      <div class="hv-updates__timeline-body">
        <div class="hv-updates__timeline-row">
          <StatusBadge status={report.status} />
          <span class="hv-updates__timeline-time"
                title={new Date(report.reportedAt).toLocaleString()}>
            {formatRelative(report.reportedAt)}
          </span>
        </div>
        {report.errorMessage && (
          <p class="hv-updates__timeline-error">{report.errorMessage}</p>
        )}
      </div>
    </li>
  );
}

// --- Kleine Bausteine ------------------------------------------------------

function StatusBadge({ status, large }: { status: RolloutTargetStatus; large?: boolean }) {
  const tone = statusTone(status);
  return (
    <span class={
      'hv-updates__badge'
      + ` hv-updates__badge--${tone}`
      + (large ? ' hv-updates__badge--large' : '')
    }>
      {status}
    </span>
  );
}

function ArtifactKindIcon({ kind }: { kind: ArtifactKind }) {
  const glyph = kindGlyph(kind);
  return (
    <span class={`hv-updates__kind-icon hv-updates__kind-icon--${kindClass(kind)}`}
          aria-hidden="true">
      {glyph}
    </span>
  );
}

function kindGlyph(kind: ArtifactKind): string {
  switch (kind) {
    case 'AndroidApk':       return 'A';
    case 'AndroidSystemOta': return 'S';
    case 'ConfigFile':       return 'C';
    case 'CertBundle':       return 'K';
    case 'ClimatixUcf':      return 'U';
    case 'GenericBlob':      return 'B';
    default:                 return '?';
  }
}

function kindClass(kind: ArtifactKind): string {
  return kind.toLowerCase();
}

function formatKind(kind: ArtifactKind): string {
  switch (kind) {
    case 'AndroidApk':       return 'Android APK';
    case 'AndroidSystemOta': return 'Android System OTA';
    case 'ConfigFile':       return 'Config-Datei';
    case 'CertBundle':       return 'Zertifikatspaket';
    case 'ClimatixUcf':      return 'Climatix .ucf';
    case 'GenericBlob':      return 'Generic Blob';
    default:                 return kind;
  }
}

function statusTone(status: RolloutTargetStatus): 'ok' | 'warn' | 'err' | 'neutral' {
  switch (status) {
    case 'Applied':                  return 'ok';
    case 'Failed':                   return 'err';
    case 'RejectedSignatureInvalid': return 'err';
    case 'Queued':                   return 'neutral';
    default:                         return 'warn';
  }
}

function isTerminal(status: RolloutTargetStatus): boolean {
  return status === 'Applied' || status === 'Failed' || status === 'RejectedSignatureInvalid';
}

// Grober "vor 3 Minuten"-Formatter. Reicht fuer die Timeline; ganze
// Locale-Bibliothek waere Overkill.
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 5) return 'gerade eben';
  if (diffSec < 60) return `vor ${diffSec} s`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `vor ${diffMin} min`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `vor ${diffHr} h`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `vor ${diffDay} d`;
  return new Date(iso).toLocaleDateString();
}
